import Tabs from "../components/General/ResponsiveTabs/ResponsiveTabs";
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Autocomplete,
  TextField,
  Button,
  Tab,
  Stack,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControlLabel,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parseISO, differenceInDays } from "date-fns";
import { getCompanies, Company } from "../api/companyService";
import { AgingReportDto } from "../interfaces/invoice";
import useAuth from "../hooks/useAuth";
import * as XLSX from "xlsx";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import axios from "axios";
import { apiVariable } from "../api/apiVariable";
import {
  getIncidentTypesForAsset,
  INCIDENT_TYPE_LABEL_MAP,
} from "../data/Fleet/IncidentType";
import { camelCaseToSentenceCase } from "../helpers/camelCaseToSentenceCase";
import { PaymentMethod } from "../data/Fleet/PaymentMethod";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useNavigate } from "react-router-dom";

const BUCKET_ORDER = ["Current", "1-30", "31-60", "61-90", "91+"];

type AssetType = "truck" | "trailer";

interface AssetOption {
  id: string;
  label: string;
  unitNumber?: string;
  assetType: AssetType;
}

interface RepairDetail {
  description: string;
  category: string | number;
  date: string;
  mileage?: number | null;
  vendor: string;
  amount: number;
  chargedAmount?: number | null;
  chargedCompanyAmount?: number | null;
  chargedDriverAmount?: number | null;
  invoiceNumber?: (string | null)[] | null;
  unitNumber?: string;
}

interface RepairModelDto {
  make: string;
  year: number;
  vin: string;
  unitNumber: string;
  tireSize: number;
  startDate: string;
  endDate: string;
  repairs: RepairDetail[];
}

interface RepairReportResponse {
  repair: RepairModelDto;
}

interface MaintenanceHistoryDetailDto {
  dateFulfilled: string;
  mileage?: number | null;
  dueIn: string;
  invoiceNumber?: (string | null)[] | null;
}

interface MaintenanceOperationDto {
  operationPerformed: string | number;
  maintenanceHistories: MaintenanceHistoryDetailDto[];
}

interface MaintenanceModelDto {
  make: string;
  year: number;
  vin: string;
  unitNumber: string;
  tireSize: number;
  startDate: string;
  endDate: string;
  operations: MaintenanceOperationDto[];
}

interface CompanyOwnerDto {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber?: number | null;
  fax?: number | null;
  DOTnumber?: number | null;
}

interface MaintenanceReportResponse {
  maintenance: MaintenanceModelDto;
  owner: CompanyOwnerDto;
}

interface LoanDto {
  loanFlag?: string;
  loanNumber?: string;
  lenderName?: string;
  loanAmount?: number;
  monthlyPayment?: number;
  loanTermMonths?: number;
  interestRate?: number;
  estimatedRemainingBalance?: number;
  loanStartDate?: string;
  loanEndDate?: string;
  estimatedEndDate?: string;
  latestVerifiedRemainingBalance?: number;
  verifiedRemainingBalance?: number;
}

interface RentDto {
  billToCompanyName?: string;
  authorityCompanyName?: string;
  transactionType?: string;
  driverFullName?: string;
  rentalTerm?: number;
  leaseTerm?: number;
  billingFrequency?: string;
  baseRentalRate?: number;
  reimbursableDeposit?: number;
  mileageRate?: number;
  remainingBalance?: number;
  startDate?: string;
  endDate?: string;
}

interface AssetDto {
  id?: string;
  unitNumber?: string;
  vin?: string;
  licensePlate?: string;
  state?: string;
  make?: string;
  model?: string;
  year?: number;
  mileage?: number;
  status?: string;
}

interface AssetWithFinancialDto {
  asset: AssetDto;
  loan?: LoanDto | null;
  rent?: RentDto | null;
  assetType: AssetType;
}

const resolveChargedTotal = (repair: RepairDetail) => {
  const companyAmount =
    repair.chargedCompanyAmount !== null &&
    repair.chargedCompanyAmount !== undefined
      ? repair.chargedCompanyAmount
      : null;
  const driverAmount =
    repair.chargedDriverAmount !== null &&
    repair.chargedDriverAmount !== undefined
      ? repair.chargedDriverAmount
      : null;

  if (companyAmount !== null || driverAmount !== null) {
    return (companyAmount ?? 0) + (driverAmount ?? 0);
  }

  return repair.chargedAmount ?? 0;
};

interface MaintenanceHistoryRow {
  id: string;
  operationLabel: string;
  dateFulfilled: string | null;
  mileage: number | null;
  dueIn: string | null;
  invoiceNumber?: (string | null)[] | null;
  isEmpty?: boolean;
}

interface MaintenancePerMileModelDto {
  startDate: string;
  endDate: string;
  distance: number;
  unitNumber: string;
  repairs: RepairDetail[];
}

interface MaintenancePerMileReportResponse {
  maintenance: MaintenancePerMileModelDto;
  totalIncidentAmount: number;
  totalYardRepairAmount: number;
  totalCharges: number;
}

interface AllRepairsModelDto {
  startDate: string;
  endDate: string;
  repairs: RepairDetail[];
}

interface AllRepairsReportResponse {
  allRepairs: AllRepairsModelDto;
  totalIncidentAmount: number;
  totalYardRepairAmount: number;
  totalCharges: number;
  maintenancePerMile?: MaintenancePerMileModelDto | null;
  totalDistance?: number | null;
}

interface AllRepairsUnitGroup {
  unitNumber: string;
  repairs: RepairDetail[];
  totalAmount: number;
  totalCharged: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 3 }, minWidth: 0 }}>{children}</Box>
      )}
    </div>
  );
}

type ReportTabKey =
  | "aging"
  | "repairs"
  | "maintenanceHistory"
  | "maintenancePerMile"
  | "allRepairs"
  | "assetFinancials";

interface ReportsProps {
  mode?: "accounting" | "fleetRental" | "fleet";
}

const Reports: React.FC<ReportsProps> = ({ mode = "accounting" }) => {
  const { token } = useAuth();
  const availableTabs = useMemo<{ key: ReportTabKey; label: string }[]>(() => {
    if (mode === "fleetRental" || mode === "fleet") {
      return [
        { key: "repairs", label: "Repairs Report" },
        { key: "maintenanceHistory", label: "Maintenance History" },
        { key: "maintenancePerMile", label: "Maintenance Per Mile" },
        { key: "allRepairs", label: "All Repairs Report" },
        { key: "assetFinancials", label: "Asset Financials" },
      ];
    }

    return [
      { key: "aging", label: "Aging Report" },
      { key: "assetFinancials", label: "Asset Financials" },
    ];
  }, [mode]);

  const [activeTab, setActiveTab] = useState<ReportTabKey>(
    availableTabs[0].key,
  );
  const [asOfDate, setAsOfDate] = useState<Date | null>(new Date());
  const [selectedIssuer, setSelectedIssuer] = useState<Company | null>(null);
  const [selectedBillTo, setSelectedBillTo] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [agingReport, setAgingReport] = useState<AgingReportDto | null>(null);
  const [truckOptions, setTruckOptions] = useState<AssetOption[]>([]);
  const [trailerOptions, setTrailerOptions] = useState<AssetOption[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState<string | null>(null);

  const [repairsAssetType, setRepairsAssetType] = useState<AssetType>("truck");
  const [selectedRepairsAsset, setSelectedRepairsAsset] =
    useState<AssetOption | null>(null);
  const [repairStartDate, setRepairStartDate] = useState<Date | null>(null);
  const [repairEndDate, setRepairEndDate] = useState<Date | null>(null);
  const [repairReport, setRepairReport] = useState<RepairReportResponse | null>(
    null,
  );
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairError, setRepairError] = useState<string | null>(null);

  const [maintenanceAssetType, setMaintenanceAssetType] =
    useState<AssetType>("truck");
  const [selectedMaintenanceAsset, setSelectedMaintenanceAsset] =
    useState<AssetOption | null>(null);
  const [maintenanceStartDate, setMaintenanceStartDate] = useState<Date | null>(
    null,
  );
  const [maintenanceEndDate, setMaintenanceEndDate] = useState<Date | null>(
    null,
  );
  const [maintenanceReport, setMaintenanceReport] =
    useState<MaintenanceReportResponse | null>(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState<string | null>(null);

  const [maintenancePerMileAssetType, setMaintenancePerMileAssetType] =
    useState<AssetType>("truck");
  const [selectedMaintenancePerMileAsset, setSelectedMaintenancePerMileAsset] =
    useState<AssetOption | null>(null);
  const [maintenancePerMileStartDate, setMaintenancePerMileStartDate] =
    useState<Date | null>(null);
  const [maintenancePerMileEndDate, setMaintenancePerMileEndDate] =
    useState<Date | null>(null);
  const [maintenancePerMileReport, setMaintenancePerMileReport] =
    useState<MaintenancePerMileReportResponse | null>(null);
  const [maintenancePerMileLoading, setMaintenancePerMileLoading] =
    useState(false);
  const [maintenancePerMileError, setMaintenancePerMileError] = useState<
    string | null
  >(null);

  const [allRepairsStartDate, setAllRepairsStartDate] = useState<Date | null>(
    null,
  );
  const [allRepairsEndDate, setAllRepairsEndDate] = useState<Date | null>(null);
  const [allRepairsForTrucks, setAllRepairsForTrucks] = useState(true);
  const [
    allRepairsIncludeMaintenancePerMile,
    setAllRepairsIncludeMaintenancePerMile,
  ] = useState(false);
  const [allRepairsScope, setAllRepairsScope] = useState<
    "all" | "incidents" | "yard"
  >("all");
  const [allRepairsMake, setAllRepairsMake] = useState<string>("");
  const [allRepairsModel, setAllRepairsModel] = useState<string>("");
  const [allRepairsTransactionType, setAllRepairsTransactionType] =
    useState<string>("");
  const [allRepairsPaymentMethods, setAllRepairsPaymentMethods] = useState<
    string[]
  >([]);
  const [allRepairsIncidentTypes, setAllRepairsIncidentTypes] = useState<
    string[]
  >([]);
  const [allRepairsUnitsByType, setAllRepairsUnitsByType] = useState<
    Record<AssetType, AssetOption[]>
  >({
    truck: [],
    trailer: [],
  });
  const [allRepairsReport, setAllRepairsReport] =
    useState<AllRepairsReportResponse | null>(null);
  const [allRepairsLoading, setAllRepairsLoading] = useState(false);
  const [allRepairsError, setAllRepairsError] = useState<string | null>(null);
  const [expandedAllRepairUnits, setExpandedAllRepairUnits] = useState<
    Set<string>
  >(new Set());

  const [financialAssetType, setFinancialAssetType] =
    useState<AssetType>("truck");
  const [financialAreActive, setFinancialAreActive] = useState(true);
  const [financialAssets, setFinancialAssets] = useState<
    AssetWithFinancialDto[]
  >([]);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [financialError, setFinancialError] = useState<string | null>(null);

  const assetOptionsByType = useMemo<Record<AssetType, AssetOption[]>>(
    () => ({
      truck: truckOptions,
      trailer: trailerOptions,
    }),
    [truckOptions, trailerOptions],
  );

  const allRepairsAssetType = useMemo<AssetType>(
    () => (allRepairsForTrucks ? "truck" : "trailer"),
    [allRepairsForTrucks],
  );

  const allRepairsSelectedUnits = useMemo(
    () => allRepairsUnitsByType[allRepairsAssetType] || [],
    [allRepairsUnitsByType, allRepairsAssetType],
  );

  const navigate = useNavigate();

  const handleAllRepairsUnitsChange = (
    _event: React.SyntheticEvent,
    newValue: AssetOption[],
  ) => {
    setAllRepairsUnitsByType((prev) => ({
      ...prev,
      [allRepairsAssetType]: newValue,
    }));
  };

  const getIncidentLabel = (category: string | number) => {
    const key = String(category);
    return INCIDENT_TYPE_LABEL_MAP[key] ?? camelCaseToSentenceCase(key);
  };

  const formatOperationLabel = (operationPerformed: string | number) => {
    if (typeof operationPerformed === "string") {
      return camelCaseToSentenceCase(operationPerformed);
    }
    return `Operation ${operationPerformed}`;
  };

  const getTimestamp = (value?: string | null) => {
    if (!value) return Number.NEGATIVE_INFINITY;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
  };

  const sortedRepairReportRepairs = useMemo<RepairDetail[]>(() => {
    if (!repairReport) return [];
    return [...repairReport.repair.repairs].sort(
      (a, b) => getTimestamp(b.date) - getTimestamp(a.date),
    );
  }, [repairReport]);

  const maintenanceHistoryRows = useMemo<MaintenanceHistoryRow[]>(() => {
    if (!maintenanceReport) return [];

    return maintenanceReport.maintenance.operations
      .flatMap<MaintenanceHistoryRow>((operation) => {
        if (operation.maintenanceHistories.length === 0) {
          return [
            {
              id: `${operation.operationPerformed}-empty`,
              operationLabel: formatOperationLabel(
                operation.operationPerformed,
              ),
              dateFulfilled: null,
              mileage: null,
              dueIn: null,
              invoiceNumber: null,
              isEmpty: true,
            },
          ];
        }

        return operation.maintenanceHistories.map((history, index) => ({
          id: `${operation.operationPerformed}-${history.dateFulfilled}-${index}`,
          operationLabel: formatOperationLabel(operation.operationPerformed),
          dateFulfilled: history.dateFulfilled,
          mileage: history.mileage ?? null,
          dueIn: history.dueIn ?? null,
          invoiceNumber: history.invoiceNumber ?? null,
          isEmpty: false,
        }));
      })
      .sort(
        (a, b) => getTimestamp(b.dateFulfilled) - getTimestamp(a.dateFulfilled),
      );
  }, [maintenanceReport]);

  const sortedMaintenancePerMileRepairs = useMemo<RepairDetail[]>(() => {
    if (!maintenancePerMileReport) return [];
    return [...maintenancePerMileReport.maintenance.repairs].sort(
      (a, b) => getTimestamp(b.date) - getTimestamp(a.date),
    );
  }, [maintenancePerMileReport]);

  const allRepairsByUnit = useMemo<AllRepairsUnitGroup[]>(() => {
    if (!allRepairsReport) return [];

    const repairsByUnit = new Map<string, RepairDetail[]>();

    allRepairsReport.allRepairs.repairs.forEach((repair) => {
      const unitLabel = repair.unitNumber ?? "Unknown Unit";
      const existingRepairs = repairsByUnit.get(unitLabel) ?? [];
      existingRepairs.push(repair);
      repairsByUnit.set(unitLabel, existingRepairs);
    });

    return Array.from(repairsByUnit.entries())
      .map(([unitNumber, repairs]) => {
        const sortedRepairs = [...repairs].sort(
          (a, b) => getTimestamp(b.date) - getTimestamp(a.date),
        );
        const totalAmount = sortedRepairs.reduce(
          (sum, repair) => sum + (repair.amount ?? 0),
          0,
        );
        const totalCharged = sortedRepairs.reduce(
          (sum, repair) => sum + resolveChargedTotal(repair),
          0,
        );

        return {
          unitNumber,
          repairs: sortedRepairs,
          totalAmount,
          totalCharged,
        };
      })
      .sort((a, b) =>
        a.unitNumber.localeCompare(b.unitNumber, undefined, {
          numeric: true,
          sensitivity: "base",
        }),
      );
  }, [allRepairsReport]);

  const maintenancePerMileTotals = useMemo(() => {
    if (!maintenancePerMileReport) return null;

    const totalIncident = maintenancePerMileReport.totalIncidentAmount;
    const totalYard = maintenancePerMileReport.totalYardRepairAmount;
    const overall = totalIncident + totalYard;
    const totalCharges = maintenancePerMileReport.totalCharges;
    const distance = maintenancePerMileReport.maintenance.distance;
    const maintenancePerMileValue =
      distance > 0 ? (overall - totalCharges) / distance : null;

    return {
      totalIncident,
      totalYard,
      overall,
      totalCharges,
      distance,
      maintenancePerMileValue,
    };
  }, [maintenancePerMileReport]);

  const allRepairsTotals = useMemo(() => {
    if (!allRepairsReport) return null;

    const totalIncident = allRepairsReport.totalIncidentAmount;
    const totalYard = allRepairsReport.totalYardRepairAmount;
    const overall = totalIncident + totalYard;
    const totalCharges = allRepairsReport.totalCharges;
    const distance = allRepairsReport.totalDistance ?? null;
    const maintenancePerMileValue =
      distance && distance > 0 ? (overall - totalCharges) / distance : null;

    return {
      totalIncident,
      totalYard,
      overall,
      totalCharges,
      distance,
      maintenancePerMileValue,
    };
  }, [allRepairsReport]);

  const formatInvoiceNumbers = (invoiceNumbers?: (string | null)[] | null) => {
    if (!invoiceNumbers || invoiceNumbers.length === 0) return "-";
    return invoiceNumbers.filter(Boolean).join(", ");
  };

  const handleToggleAllRepairsUnit = (unitNumber: string) => {
    setExpandedAllRepairUnits((prev) => {
      const updated = new Set(prev);
      if (updated.has(unitNumber)) {
        updated.delete(unitNumber);
      } else {
        updated.add(unitNumber);
      }
      return updated;
    });
  };

  useEffect(() => {
    setExpandedAllRepairUnits(new Set());
  }, [allRepairsReport]);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const data = await getCompanies(token);
        setCompanies(data);
      } catch (error) {
        console.error("Failed to load companies:", error);
      }
    };

    loadCompanies();
  }, [token]);

  useEffect(() => {
    if (!token) return;

    const fetchAssets = async () => {
      setAssetsLoading(true);
      setAssetsError(null);
      try {
        const [trucksResponse, trailersResponse] = await Promise.all([
          axios.get(`${apiVariable}/getAllTrucks`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { areActive: true },
          }),
          axios.get(`${apiVariable}/getAllTrailers`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { areActive: true },
          }),
        ]);

        const trucks = (trucksResponse.data || []).map((truck: any) => ({
          id: truck.id,
          label: truck.unitNumber ?? "",
          unitNumber: truck.unitNumber,
          assetType: "truck" as const,
        }));

        const trailers = (trailersResponse.data || []).map((trailer: any) => ({
          id: trailer.id,
          label: trailer.unitNumber ?? "",
          unitNumber: trailer.unitNumber,
          assetType: "trailer" as const,
        }));

        setTruckOptions(trucks);
        setTrailerOptions(trailers);
      } catch (error) {
        console.error("Failed to load assets", error);
        setAssetsError("Failed to load assets");
      } finally {
        setAssetsLoading(false);
      }
    };

    fetchAssets();
  }, [token]);

  useEffect(() => {
    const hasActiveTab = availableTabs.some((tab) => tab.key === activeTab);
    if (!hasActiveTab) {
      setActiveTab(availableTabs[0].key);
    }
  }, [availableTabs, activeTab]);

  const tabValue = Math.max(
    0,
    availableTabs.findIndex((tab) => tab.key === activeTab),
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(availableTabs[newValue].key);
  };

  useEffect(() => {
    if (!token || activeTab !== "assetFinancials") return;

    const fetchFinancialAssets = async () => {
      setFinancialLoading(true);
      setFinancialError(null);

      try {
        const endpoint =
          financialAssetType === "truck"
            ? "getAllTrucksWithFinancial"
            : "getAllTrailersWithFinancial";
        const { data } = await axios.get(`${apiVariable}/${endpoint}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { areActive: financialAreActive },
        });

        const normalized = (data || []).map((item: any) => ({
          asset: item.truck ?? item.trailer ?? {},
          loan: item.loan ?? null,
          rent: item.rent ?? null,
          assetType: financialAssetType,
        }));

        setFinancialAssets(normalized);
      } catch (error) {
        console.error("Failed to load financial assets", error);
        setFinancialError("Failed to load asset financials");
      } finally {
        setFinancialLoading(false);
      }
    };

    fetchFinancialAssets();
  }, [token, activeTab, financialAssetType, financialAreActive]);

  const handleGenerateAgingReport = async () => {
    if (!asOfDate || !selectedIssuer) return;

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL
        }/aging?asOf=${asOfDate.toISOString()}&issuerId=${selectedIssuer.id}${
          selectedBillTo ? `&billToId=${selectedBillTo.id}` : ""
        }`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) throw new Error("Failed to generate aging report");
      const data = await response.json();
      setAgingReport(data);
    } catch (error) {
      console.error("Failed to generate aging report:", error);
    }
  };

  const buildDateParams = (startDate: Date, endDate: Date) => {
    const params = new URLSearchParams();
    params.set("startDate", startDate.toISOString());
    params.set("endDate", endDate.toISOString());
    return params;
  };

  const handleGenerateRepairReport = async () => {
    if (!selectedRepairsAsset || !repairStartDate || !repairEndDate) {
      setRepairError("Please select an asset and date range");
      return;
    }

    setRepairLoading(true);
    setRepairError(null);
    setRepairReport(null);

    try {
      const params = buildDateParams(repairStartDate, repairEndDate);
      const response = await fetch(
        `${apiVariable}/getRepairReportData/${
          selectedRepairsAsset.id
        }/${repairsAssetType}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to load repair report");
      }

      const data = (await response.json()) as RepairReportResponse;
      setRepairReport(data);
    } catch (error: any) {
      console.error("Failed to load repair report", error);
      setRepairError(error.message || "Failed to load repair report");
    } finally {
      setRepairLoading(false);
    }
  };

  const handleGenerateMaintenanceReport = async () => {
    if (
      !selectedMaintenanceAsset ||
      !maintenanceStartDate ||
      !maintenanceEndDate
    ) {
      setMaintenanceError("Please select an asset and date range");
      return;
    }

    setMaintenanceLoading(true);
    setMaintenanceError(null);
    setMaintenanceReport(null);

    try {
      const params = buildDateParams(maintenanceStartDate, maintenanceEndDate);
      const response = await fetch(
        `${apiVariable}/getMaintenanceReportData/${
          selectedMaintenanceAsset.id
        }/${maintenanceAssetType}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to load maintenance report");
      }

      const data = (await response.json()) as MaintenanceReportResponse;
      setMaintenanceReport(data);
    } catch (error: any) {
      console.error("Failed to load maintenance report", error);
      setMaintenanceError(error.message || "Failed to load maintenance report");
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const handleGenerateMaintenancePerMileReport = async () => {
    if (
      !selectedMaintenancePerMileAsset ||
      !maintenancePerMileStartDate ||
      !maintenancePerMileEndDate
    ) {
      setMaintenancePerMileError("Please select an asset and date range");
      return;
    }

    setMaintenancePerMileLoading(true);
    setMaintenancePerMileError(null);
    setMaintenancePerMileReport(null);

    try {
      const params = buildDateParams(
        maintenancePerMileStartDate,
        maintenancePerMileEndDate,
      );
      const response = await fetch(
        `${apiVariable}/getMaintenancePerMileReportData/${
          selectedMaintenancePerMileAsset.id
        }?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText || "Failed to load maintenance per mile report",
        );
      }

      const data = (await response.json()) as MaintenancePerMileReportResponse;
      setMaintenancePerMileReport(data);
    } catch (error: any) {
      console.error("Failed to load maintenance per mile report", error);
      setMaintenancePerMileError(
        error.message || "Failed to load maintenance per mile report",
      );
    } finally {
      setMaintenancePerMileLoading(false);
    }
  };

  const handleGenerateAllRepairsReport = async () => {
    if (!allRepairsStartDate || !allRepairsEndDate) {
      setAllRepairsError("Please select a date range");
      return;
    }

    setAllRepairsLoading(true);
    setAllRepairsError(null);
    setAllRepairsReport(null);

    try {
      const params = new URLSearchParams();
      params.set("startDate", allRepairsStartDate.toISOString());
      params.set("endDate", allRepairsEndDate.toISOString());
      params.set("isForTrucks", String(allRepairsForTrucks));
      params.set(
        "includeMaintenancePerMile",
        String(allRepairsIncludeMaintenancePerMile),
      );

      if (allRepairsScope !== "all") {
        params.set(
          "incidentsRepairsOnly",
          String(allRepairsScope === "incidents"),
        );
      }

      if (allRepairsMake.trim()) {
        params.set("make", allRepairsMake.trim());
      }

      if (allRepairsModel.trim()) {
        params.set("model", allRepairsModel.trim());
      }

      if (allRepairsTransactionType) {
        params.set("rentTransactionType", allRepairsTransactionType);
      }

      allRepairsPaymentMethods.forEach((method) =>
        params.append("paymentMethods", method),
      );
      allRepairsIncidentTypes.forEach((incident) =>
        params.append("incidentTypes", incident),
      );

      const selectedUnits = allRepairsUnitsByType[allRepairsAssetType];
      selectedUnits.forEach((unit) => {
        if (unit.unitNumber) {
          params.append("unitNumbers", unit.unitNumber);
        }
      });

      const response = await fetch(
        `${apiVariable}/getAllRepairReportData?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to load repairs report");
      }

      const data = (await response.json()) as AllRepairsReportResponse;
      setAllRepairsReport(data);
    } catch (error: any) {
      console.error("Failed to load all repairs report", error);
      setAllRepairsError(error.message || "Failed to load repairs report");
    } finally {
      setAllRepairsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDateValue = (value?: string | null) => {
    if (!value) return "N/A";
    const parsed = parseISO(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return format(parsed, "MM/dd/yyyy");
  };

  const formatPercent = (value?: number | null) => {
    if (value === null || value === undefined) return "N/A";
    return `${value}%`;
  };

  const getSummaryData = () => {
    if (!agingReport) return [];

    const groups = agingReport.invoices.reduce(
      (acc, invoice) => {
        if (!acc[invoice.billTo]) {
          acc[invoice.billTo] = {
            company: invoice.billTo,
            buckets: BUCKET_ORDER.reduce(
              (b, bucket) => ({ ...b, [bucket]: 0 }),
              {},
            ),
          };
        }
        acc[invoice.billTo].buckets[invoice.bucket] += invoice.balance;
        return acc;
      },
      {} as Record<
        string,
        { company: string; buckets: Record<string, number> }
      >,
    );

    return Object.values(groups);
  };

  const exportToExcel = () => {
    if (!agingReport) return;

    const wb = XLSX.utils.book_new();

    const summaryData = getSummaryData().map((group) => {
      const overdue = BUCKET_ORDER.slice(1).reduce(
        (sum, bucket) => sum + group.buckets[bucket],
        0,
      );
      const total = Object.values(group.buckets).reduce(
        (sum, amount) => sum + amount,
        0,
      );

      return {
        CUSTOMER: group.company,
        ...BUCKET_ORDER.reduce(
          (acc, bucket) => ({
            ...acc,
            [bucket]: group.buckets[bucket],
          }),
          {},
        ),
        "OVER-DUE": overdue,
        TOTAL: total,
      };
    });

    type SummaryData = Array<{
      CUSTOMER: string;
      "OVER-DUE": number;
      TOTAL: number;
      [key: string]: string | number;
    }>;

    const grandTotals = {
      "OVER-DUE": summaryData.reduce(
        (sum, row) => sum + (row["OVER-DUE"] as number),
        0,
      ),
      TOTAL: summaryData.reduce(
        (sum, row) => sum + (row["TOTAL"] as number),
        0,
      ),
      ...BUCKET_ORDER.reduce(
        (acc, bucket) => ({
          ...acc,
          [bucket]: summaryData.reduce(
            (sum, row) => sum + ((row as any)[bucket] as number),
            0,
          ),
        }),
        {},
      ),
    };

    (summaryData as SummaryData).push({
      CUSTOMER: "GRAND TOTAL",
      ...grandTotals,
    });

    const summaryWs = XLSX.utils.json_to_sheet(summaryData);

    const detailData = agingReport.invoices
      .sort((a, b) => {
        if (a.billTo !== b.billTo) return a.billTo.localeCompare(b.billTo);
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      })
      .map((invoice) => {
        const days = differenceInDays(
          parseISO(agingReport.asOf),
          parseISO(invoice.dueDate),
        );

        return {
          CUSTOMER: invoice.billTo,
          UNIT_NUMBER: invoice.unitNumber,
          ASSET_TYPE: invoice.assetType,
          NO: invoice.invoiceNumber,
          DATE: format(parseISO(invoice.invoiceDate), "MM/dd/yy"),
          DUE: format(parseISO(invoice.dueDate), "MM/dd/yy"),
          DAYS: days,
          ...BUCKET_ORDER.reduce(
            (acc, bucket) => ({
              ...acc,
              [bucket]: invoice.bucket === bucket ? invoice.balance : "",
            }),
            {},
          ),
          "ORIGINAL INVOICE AMOUNT": invoice.total,
        };
      });

    const detailWs = XLSX.utils.json_to_sheet(detailData);

    XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");
    XLSX.utils.book_append_sheet(wb, detailWs, "Detail");

    const filename = `Aging_Report_${agingReport.issuer}_${format(
      parseISO(agingReport.asOf),
      "yyyy-MM-dd",
    )}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  const exportAllRepairsReportToExcel = () => {
    if (!allRepairsReport) return;

    const wb = XLSX.utils.book_new();
    const wsData: (string | number | null)[][] = [];

    wsData.push([
      "All Repairs Report",
      allRepairsForTrucks ? "Trucks" : "Trailers",
    ]);
    wsData.push([
      "Date Range",
      `${format(
        parseISO(allRepairsReport.allRepairs.startDate),
        "MM/dd/yyyy",
      )} - ${format(
        parseISO(allRepairsReport.allRepairs.endDate),
        "MM/dd/yyyy",
      )}`,
    ]);

    if (allRepairsTotals) {
      wsData.push([]);
      wsData.push(["Total Incident Amount", allRepairsTotals.totalIncident]);
      wsData.push(["Total Yard Repair Amount", allRepairsTotals.totalYard]);
      wsData.push(["Overall Repair Amount", allRepairsTotals.overall]);
      wsData.push(["Total Charges", allRepairsTotals.totalCharges]);
      if (allRepairsTotals.distance != null) {
        wsData.push(["Total Distance", allRepairsTotals.distance]);
      }
      wsData.push([
        "Maintenance Per Mile",
        allRepairsTotals.maintenancePerMileValue ?? "N/A",
      ]);
    }

    wsData.push([]);
    wsData.push([
      "Unit",
      "Repairs",
      "Total Amount",
      "Total Charged",
      "",
      "",
      "",
      "",
      "",
    ]);

    allRepairsByUnit.forEach((unitGroup) => {
      wsData.push([
        unitGroup.unitNumber,
        unitGroup.repairs.length,
        unitGroup.totalAmount,
        unitGroup.totalCharged,
        "",
        "",
        "",
        "",
        "",
      ]);

      wsData.push([
        "",
        "Description",
        "Category",
        "Date",
        "Mileage",
        "Vendor",
        "Amount",
        "Charged",
        "Invoice #",
      ]);

      unitGroup.repairs.forEach((repair) => {
        wsData.push([
          "",
          repair.description,
          getIncidentLabel(repair.category),
          format(parseISO(repair.date), "MM/dd/yyyy"),
          repair.mileage ?? "",
          repair.vendor,
          repair.amount,
          resolveChargedTotal(repair),
          formatInvoiceNumbers(repair.invoiceNumber),
        ]);
      });

      wsData.push([]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "All Repairs");

    const filename = `All_Repairs_Report_${
      allRepairsForTrucks ? "Trucks" : "Trailers"
    }_${format(
      parseISO(allRepairsReport.allRepairs.startDate),
      "yyyy-MM-dd",
    )}_${format(
      parseISO(allRepairsReport.allRepairs.endDate),
      "yyyy-MM-dd",
    )}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Reports
      </Typography>

      <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable">
        {availableTabs.map((tab) => (
          <Tab key={tab.key} label={tab.label} />
        ))}
      </Tabs>

      <TabPanel
        value={tabValue}
        index={availableTabs.findIndex((tab) => tab.key === "aging")}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              flexWrap: { xs: "wrap", lg: "nowrap" },
              display: "flex",
              gap: 2,
              mb: 2,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="As of Date"
                value={asOfDate}
                onChange={(newValue) => setAsOfDate(newValue)}
              />
            </LocalizationProvider>
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.name}
              value={selectedIssuer}
              onChange={(_, newValue) => setSelectedIssuer(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Issuer" required />
              )}
              sx={{ maxWidth: "100%", width: 300 }}
            />
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.name}
              value={selectedBillTo}
              onChange={(_, newValue) => setSelectedBillTo(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Bill To (Optional)" />
              )}
              sx={{ maxWidth: "100%", width: 300 }}
            />
            <Button
              variant="contained"
              onClick={handleGenerateAgingReport}
              disabled={!asOfDate || !selectedIssuer}
            >
              Generate Report
            </Button>
          </Box>

          {agingReport && (
            <>
              <Stack
                flexWrap={{ xs: "wrap", lg: "nowrap" }}
                useFlexGap
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6">
                    A/R AGING REPORT – {agingReport.issuer}
                  </Typography>
                  <Typography variant="subtitle1">
                    As of {format(parseISO(agingReport.asOf), "MMMM dd, yyyy")}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportToExcel}
                >
                  Export to Excel
                </Button>
              </Stack>

              <Typography variant="h6" sx={{ mb: 2 }}>
                Summary by Customer
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      {BUCKET_ORDER.map((bucket) => (
                        <TableCell key={bucket} align="right">
                          {bucket}
                        </TableCell>
                      ))}
                      <TableCell align="right">Over-due</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSummaryData().map((group) => {
                      const overdue = BUCKET_ORDER.slice(1).reduce(
                        (sum, bucket) => sum + group.buckets[bucket],
                        0,
                      );
                      const total = Object.values(group.buckets).reduce(
                        (sum, amount) => sum + amount,
                        0,
                      );

                      return (
                        <TableRow key={group.company}>
                          <TableCell>{group.company}</TableCell>
                          {BUCKET_ORDER.map((bucket) => (
                            <TableCell key={bucket} align="right">
                              {formatCurrency(group.buckets[bucket])}
                            </TableCell>
                          ))}
                          <TableCell
                            align="right"
                            sx={{
                              color: overdue > 0 ? "error.main" : "inherit",
                            }}
                          >
                            {formatCurrency(overdue)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(total)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" sx={{ mb: 2 }}>
                Invoice Detail
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Unit number</TableCell>
                      <TableCell>Asset Type</TableCell>
                      <TableCell>No</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Due</TableCell>
                      <TableCell>Days</TableCell>
                      {BUCKET_ORDER.map((bucket) => (
                        <TableCell key={bucket} align="right">
                          {bucket}
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        Original Invoice Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agingReport.invoices
                      .sort((a, b) => {
                        if (a.billTo !== b.billTo)
                          return a.billTo.localeCompare(b.billTo);
                        return (
                          new Date(a.dueDate).getTime() -
                          new Date(b.dueDate).getTime()
                        );
                      })
                      .map((invoice, index) => {
                        const days = differenceInDays(
                          parseISO(agingReport.asOf),
                          parseISO(invoice.dueDate),
                        );
                        const isLate = days > 0;

                        return (
                          <TableRow key={index}>
                            <TableCell>{invoice.billTo}</TableCell>
                            <TableCell>{invoice.unitNumber}</TableCell>
                            <TableCell>{invoice.assetType}</TableCell>
                            <TableCell>{invoice.invoiceNumber}</TableCell>
                            <TableCell>
                              {format(
                                parseISO(invoice.invoiceDate),
                                "MM/dd/yy",
                              )}
                            </TableCell>
                            <TableCell>
                              {format(parseISO(invoice.dueDate), "MM/dd/yy")}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                color: isLate ? "error.main" : "inherit",
                              }}
                            >
                              {days}
                            </TableCell>
                            {BUCKET_ORDER.map((bucket) => (
                              <TableCell key={bucket} align="right">
                                {invoice.bucket === bucket
                                  ? formatCurrency(invoice.balance)
                                  : ""}
                              </TableCell>
                            ))}
                            <TableCell align="right">
                              {formatCurrency(invoice.total)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </TabPanel>
      <TabPanel
        value={tabValue}
        index={availableTabs.findIndex((tab) => tab.key === "repairs")}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <ToggleButtonGroup
              value={repairsAssetType}
              exclusive
              onChange={(_, value: AssetType | null) => {
                if (value) {
                  setRepairsAssetType(value);
                  setSelectedRepairsAsset(null);
                }
              }}
              color="primary"
              size="small"
            >
              <ToggleButton value="truck">Trucks</ToggleButton>
              <ToggleButton value="trailer">Trailers</ToggleButton>
            </ToggleButtonGroup>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={repairStartDate}
                onChange={setRepairStartDate}
                slotProps={{ textField: { required: true } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={repairEndDate}
                onChange={setRepairEndDate}
                slotProps={{ textField: { required: true } }}
              />
            </LocalizationProvider>
            <Autocomplete
              options={assetOptionsByType[repairsAssetType]}
              value={selectedRepairsAsset}
              onChange={(_, newValue) => setSelectedRepairsAsset(newValue)}
              getOptionLabel={(option) => option.label}
              loading={assetsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Asset"
                  required
                  helperText={assetsError ?? undefined}
                  error={Boolean(assetsError)}
                />
              )}
              sx={{ minWidth: { xs: 0, sm: 260 } }}
            />
            <Button
              variant="contained"
              onClick={handleGenerateRepairReport}
              disabled={repairLoading}
            >
              {repairLoading ? <CircularProgress size={24} /> : "Generate"}
            </Button>
          </Stack>
          {repairError && <Typography color="error">{repairError}</Typography>}
          {repairReport && (
            <Box>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6">
                    Repairs Report – {repairReport.repair.unitNumber}
                  </Typography>
                  <Typography variant="subtitle1">
                    {repairReport.repair.year} · {repairReport.repair.make} ·
                    VIN {repairReport.repair.vin}
                  </Typography>
                  <Typography variant="subtitle2">
                    {format(
                      parseISO(repairReport.repair.startDate),
                      "MM/dd/yyyy",
                    )}{" "}
                    –{" "}
                    {format(
                      parseISO(repairReport.repair.endDate),
                      "MM/dd/yyyy",
                    )}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" align="right">
                    Total Amount:{" "}
                    {formatCurrency(
                      repairReport.repair.repairs.reduce(
                        (total, repair) => total + repair.amount,
                        0,
                      ),
                    )}
                  </Typography>
                  <Typography variant="subtitle2" align="right">
                    Total Charged:{" "}
                    {formatCurrency(
                      repairReport.repair.repairs.reduce(
                        (total, repair) => total + resolveChargedTotal(repair),
                        0,
                      ),
                    )}
                  </Typography>
                </Box>
              </Stack>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Mileage</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Charged</TableCell>
                      <TableCell>Invoice #</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedRepairReportRepairs.map((repair, index) => (
                      <TableRow
                        key={`${repair.description}-${repair.date}-${index}`}
                      >
                        <TableCell>{repair.description}</TableCell>
                        <TableCell>
                          {getIncidentLabel(repair.category)}
                        </TableCell>
                        <TableCell>
                          {format(parseISO(repair.date), "MM/dd/yyyy")}
                        </TableCell>
                        <TableCell align="right">
                          {repair.mileage != null ? repair.mileage : "-"}
                        </TableCell>
                        <TableCell>{repair.vendor}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(repair.amount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(resolveChargedTotal(repair))}
                        </TableCell>
                        <TableCell>
                          {formatInvoiceNumbers(repair.invoiceNumber)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </TabPanel>
      <TabPanel
        value={tabValue}
        index={availableTabs.findIndex(
          (tab) => tab.key === "maintenanceHistory",
        )}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <ToggleButtonGroup
              value={maintenanceAssetType}
              exclusive
              onChange={(_, value: AssetType | null) => {
                if (value) {
                  setMaintenanceAssetType(value);
                  setSelectedMaintenanceAsset(null);
                }
              }}
              color="primary"
              size="small"
            >
              <ToggleButton value="truck">Trucks</ToggleButton>
              <ToggleButton value="trailer">Trailers</ToggleButton>
            </ToggleButtonGroup>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={maintenanceStartDate}
                onChange={setMaintenanceStartDate}
                slotProps={{ textField: { required: true } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={maintenanceEndDate}
                onChange={setMaintenanceEndDate}
                slotProps={{ textField: { required: true } }}
              />
            </LocalizationProvider>
            <Autocomplete
              options={assetOptionsByType[maintenanceAssetType]}
              value={selectedMaintenanceAsset}
              onChange={(_, newValue) => setSelectedMaintenanceAsset(newValue)}
              getOptionLabel={(option) => option.label}
              loading={assetsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Asset"
                  required
                  helperText={assetsError ?? undefined}
                  error={Boolean(assetsError)}
                />
              )}
              sx={{ minWidth: { xs: 0, sm: 260 } }}
            />
            <Button
              variant="contained"
              onClick={handleGenerateMaintenanceReport}
              disabled={maintenanceLoading}
            >
              {maintenanceLoading ? <CircularProgress size={24} /> : "Generate"}
            </Button>
          </Stack>
          {maintenanceError && (
            <Typography color="error">{maintenanceError}</Typography>
          )}
          {maintenanceReport && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Box>
                  <Typography variant="h6">
                    Maintenance History –{" "}
                    {maintenanceReport.maintenance.unitNumber}
                  </Typography>
                  <Typography variant="subtitle1">
                    {maintenanceReport.maintenance.make} · VIN{" "}
                    {maintenanceReport.maintenance.vin}
                  </Typography>
                  <Typography variant="subtitle2">
                    {format(
                      parseISO(maintenanceReport.maintenance.startDate),
                      "MM/dd/yyyy",
                    )}{" "}
                    –{" "}
                    {format(
                      parseISO(maintenanceReport.maintenance.endDate),
                      "MM/dd/yyyy",
                    )}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1">
                    Owner: {maintenanceReport.owner.name}
                  </Typography>
                  <Typography variant="body2">
                    {maintenanceReport.owner.address},{" "}
                    {maintenanceReport.owner.city},{" "}
                    {maintenanceReport.owner.state}{" "}
                    {maintenanceReport.owner.zip}
                  </Typography>
                </Box>
              </Stack>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Operation</TableCell>
                      <TableCell>Date Fulfilled</TableCell>
                      <TableCell align="right">Mileage</TableCell>
                      <TableCell>Due In</TableCell>
                      <TableCell>Invoice #</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {maintenanceHistoryRows.map((row) =>
                      row.isEmpty ? (
                        <TableRow key={row.id}>
                          <TableCell>{row.operationLabel}</TableCell>
                          <TableCell colSpan={4}>
                            No maintenance history available
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow key={row.id}>
                          <TableCell>{row.operationLabel}</TableCell>
                          <TableCell>
                            {row.dateFulfilled
                              ? format(
                                  parseISO(row.dateFulfilled),
                                  "MM/dd/yyyy",
                                )
                              : "-"}
                          </TableCell>
                          <TableCell align="right">
                            {row.mileage != null ? row.mileage : "-"}
                          </TableCell>
                          <TableCell>{row.dueIn ?? "-"}</TableCell>
                          <TableCell>
                            {formatInvoiceNumbers(row.invoiceNumber)}
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </TabPanel>
      <TabPanel
        value={tabValue}
        index={availableTabs.findIndex(
          (tab) => tab.key === "maintenancePerMile",
        )}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <ToggleButtonGroup
              value={maintenancePerMileAssetType}
              exclusive
              onChange={(_, value: AssetType | null) => {
                if (value) {
                  setMaintenancePerMileAssetType(value);
                  setSelectedMaintenancePerMileAsset(null);
                }
              }}
              color="primary"
              size="small"
            >
              <ToggleButton value="truck">Trucks</ToggleButton>
              {/* <ToggleButton value="trailer">Trailers</ToggleButton> */}
            </ToggleButtonGroup>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={maintenancePerMileStartDate}
                onChange={setMaintenancePerMileStartDate}
                slotProps={{ textField: { required: true } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={maintenancePerMileEndDate}
                onChange={setMaintenancePerMileEndDate}
                slotProps={{ textField: { required: true } }}
              />
            </LocalizationProvider>
            <Autocomplete
              options={assetOptionsByType[maintenancePerMileAssetType]}
              value={selectedMaintenancePerMileAsset}
              onChange={(_, newValue) =>
                setSelectedMaintenancePerMileAsset(newValue)
              }
              getOptionLabel={(option) => option.label}
              loading={assetsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Asset"
                  required
                  helperText={assetsError ?? undefined}
                  error={Boolean(assetsError)}
                />
              )}
              sx={{ minWidth: { xs: 0, sm: 260 } }}
            />
            <Button
              variant="contained"
              onClick={handleGenerateMaintenancePerMileReport}
              disabled={maintenancePerMileLoading}
            >
              {maintenancePerMileLoading ? (
                <CircularProgress size={24} />
              ) : (
                "Generate"
              )}
            </Button>
          </Stack>
          {maintenancePerMileError && (
            <Typography color="error">{maintenancePerMileError}</Typography>
          )}
          {maintenancePerMileReport && (
            <Box>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6">
                    Maintenance Per Mile –{" "}
                    {maintenancePerMileReport.maintenance.unitNumber}
                  </Typography>
                  <Typography variant="subtitle2">
                    {format(
                      parseISO(maintenancePerMileReport.maintenance.startDate),
                      "MM/dd/yyyy",
                    )}{" "}
                    –{" "}
                    {format(
                      parseISO(maintenancePerMileReport.maintenance.endDate),
                      "MM/dd/yyyy",
                    )}
                  </Typography>
                </Box>
              </Stack>
              {maintenancePerMileTotals && (
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={4}
                  sx={{ mt: 2, mb: 2 }}
                >
                  <Typography>
                    Distance:{" "}
                    {maintenancePerMileTotals.distance.toLocaleString()} miles
                  </Typography>
                  <Typography>
                    Total Incident Amount:{" "}
                    {formatCurrency(maintenancePerMileTotals.totalIncident)}
                  </Typography>
                  <Typography>
                    Total Yard Repair Amount:{" "}
                    {formatCurrency(maintenancePerMileTotals.totalYard)}
                  </Typography>
                  <Typography>
                    Overall Repair Amount:{" "}
                    {formatCurrency(maintenancePerMileTotals.overall)}
                  </Typography>
                  <Typography>
                    Total Charges:{" "}
                    {formatCurrency(maintenancePerMileTotals.totalCharges)}
                  </Typography>
                  <Typography>
                    Maintenance Per Mile:{" "}
                    {maintenancePerMileTotals.maintenancePerMileValue != null
                      ? `${formatCurrency(
                          maintenancePerMileTotals.maintenancePerMileValue,
                        )} per mile`
                      : "N/A"}
                  </Typography>
                </Stack>
              )}
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Mileage</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Charged</TableCell>
                      <TableCell>Invoice #</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedMaintenancePerMileRepairs.map((repair, index) => (
                      <TableRow
                        key={`${repair.description}-${repair.date}-${index}`}
                      >
                        <TableCell>{repair.description}</TableCell>
                        <TableCell>
                          {getIncidentLabel(repair.category)}
                        </TableCell>
                        <TableCell>
                          {format(parseISO(repair.date), "MM/dd/yyyy")}
                        </TableCell>
                        <TableCell align="right">
                          {repair.mileage != null ? repair.mileage : "-"}
                        </TableCell>
                        <TableCell>{repair.vendor}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(repair.amount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(resolveChargedTotal(repair))}
                        </TableCell>
                        <TableCell>
                          {formatInvoiceNumbers(repair.invoiceNumber)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </TabPanel>
      <TabPanel
        value={tabValue}
        index={availableTabs.findIndex((tab) => tab.key === "allRepairs")}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={allRepairsStartDate}
                onChange={setAllRepairsStartDate}
                slotProps={{ textField: { required: true } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={allRepairsEndDate}
                onChange={setAllRepairsEndDate}
                slotProps={{ textField: { required: true } }}
              />
            </LocalizationProvider>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allRepairsForTrucks}
                  onChange={(event) => {
                    setAllRepairsForTrucks(event.target.checked);
                    setAllRepairsIncidentTypes([]);
                  }}
                />
              }
              label={allRepairsForTrucks ? "Trucks" : "Trailers"}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={allRepairsIncludeMaintenancePerMile}
                  onChange={(event) =>
                    setAllRepairsIncludeMaintenancePerMile(event.target.checked)
                  }
                />
              }
              label="Include Maintenance Per Mile"
            />
            <FormControl sx={{ minWidth: { xs: 0, sm: 200 } }}>
              <InputLabel id="repairs-scope-label">Repair Type</InputLabel>
              <Select
                labelId="repairs-scope-label"
                value={allRepairsScope}
                label="Repair Type"
                onChange={(event) =>
                  setAllRepairsScope(
                    event.target.value as typeof allRepairsScope,
                  )
                }
              >
                <MenuItem value="all">All Repairs</MenuItem>
                <MenuItem value="incidents">OTR Repairs Only</MenuItem>
                <MenuItem value="yard">Yard Repairs Only</MenuItem>
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              options={assetOptionsByType[allRepairsAssetType]}
              value={allRepairsSelectedUnits}
              onChange={handleAllRepairsUnitsChange}
              filterSelectedOptions
              getOptionLabel={(option) => option.unitNumber ?? option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={assetsLoading}
              renderTags={(value, getTagProps) => {
                const visibleTags = value.slice(0, 2);
                const hiddenTags = value.slice(2);

                return [
                  ...visibleTags.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.unitNumber ?? option.label}
                      size="small"
                    />
                  )),
                  hiddenTags.length > 0 && (
                    <Tooltip
                      key="all-repairs-units-more"
                      title={hiddenTags
                        .map((option) => option.unitNumber ?? option.label)
                        .join(", ")}
                    >
                      <Chip label={`+${hiddenTags.length}`} size="small" />
                    </Tooltip>
                  ),
                ].filter(Boolean);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Unit Number(s)"
                  placeholder={
                    allRepairsAssetType === "truck"
                      ? "Select trucks"
                      : "Select trailers"
                  }
                  helperText={assetsError ? "Unable to load assets" : undefined}
                />
              )}
              sx={{ minWidth: { xs: 0, sm: 260 } }}
            />
            <Button
              variant="contained"
              onClick={handleGenerateAllRepairsReport}
              disabled={allRepairsLoading}
            >
              {allRepairsLoading ? <CircularProgress size={24} /> : "Generate"}
            </Button>
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Make"
              value={allRepairsMake}
              onChange={(event) => setAllRepairsMake(event.target.value)}
            />
            <TextField
              label="Model"
              value={allRepairsModel}
              onChange={(event) => setAllRepairsModel(event.target.value)}
            />
            <FormControl sx={{ minWidth: { xs: 0, sm: 220 } }}>
              <InputLabel id="transaction-type-label">
                Transaction Type
              </InputLabel>
              <Select
                labelId="transaction-type-label"
                value={allRepairsTransactionType}
                label="Transaction Type"
                onChange={(event) =>
                  setAllRepairsTransactionType(event.target.value)
                }
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                <MenuItem value="RENT-IN-LOCAL">Rent In Local</MenuItem>
                <MenuItem value="RENT-IN-OTR">Rent In OTR</MenuItem>
                <MenuItem value="L2B">L2B</MenuItem>
                <MenuItem value="RENT-OUT">Rent Out</MenuItem>
                <MenuItem value="LEASE-OUT">Lease Out</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { xs: 0, sm: 260 } }}>
              <InputLabel id="payment-methods-label">
                Payment Method(s)
              </InputLabel>
              <Select
                labelId="payment-methods-label"
                multiple
                value={allRepairsPaymentMethods}
                label="Payment Method(s)"
                onChange={(event) =>
                  setAllRepairsPaymentMethods(event.target.value as string[])
                }
                renderValue={(selected) =>
                  (selected as string[])
                    .map(
                      (value) =>
                        PaymentMethod.find((method) => method.value === value)
                          ?.label ?? value,
                    )
                    .join(", ")
                }
              >
                {PaymentMethod.filter((method) => method.value).map(
                  (method) => (
                    <MenuItem key={method.value} value={method.value}>
                      <Checkbox
                        checked={allRepairsPaymentMethods.includes(
                          method.value,
                        )}
                      />
                      <ListItemText primary={method.label} />
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { xs: 0, sm: 220 } }}>
              <InputLabel id="incident-types-label">
                Incident Type(s)
              </InputLabel>
              <Select
                labelId="incident-types-label"
                multiple
                value={allRepairsIncidentTypes}
                label="Incident Type(s)"
                onChange={(event) =>
                  setAllRepairsIncidentTypes(event.target.value as string[])
                }
                renderValue={(selected) =>
                  (selected as string[])
                    .map((value) => INCIDENT_TYPE_LABEL_MAP[value] ?? value)
                    .join(", ")
                }
              >
                {getIncidentTypesForAsset(allRepairsAssetType)
                  .filter((incident) => incident.value)
                  .map((incident) => (
                    <MenuItem key={incident.value} value={incident.value}>
                      <Checkbox
                        checked={allRepairsIncidentTypes.includes(
                          incident.value,
                        )}
                      />
                      <ListItemText primary={incident.label} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
          {allRepairsError && (
            <Typography color="error">{allRepairsError}</Typography>
          )}
          {allRepairsReport && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Box>
                  <Typography variant="h6">
                    All Repairs Report –{" "}
                    {allRepairsForTrucks ? "Trucks" : "Trailers"}
                  </Typography>
                  <Typography variant="subtitle2">
                    {format(
                      parseISO(allRepairsReport.allRepairs.startDate),
                      "MM/dd/yyyy",
                    )}{" "}
                    –{" "}
                    {format(
                      parseISO(allRepairsReport.allRepairs.endDate),
                      "MM/dd/yyyy",
                    )}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportAllRepairsReportToExcel}
                >
                  Export to Excel
                </Button>
              </Stack>
              {allRepairsTotals && (
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Typography>
                    Total Incident Amount:{" "}
                    {formatCurrency(allRepairsTotals.totalIncident)}
                  </Typography>
                  <Typography>
                    Total Yard Repair Amount:{" "}
                    {formatCurrency(allRepairsTotals.totalYard)}
                  </Typography>
                  <Typography>
                    Overall Repair Amount:{" "}
                    {formatCurrency(allRepairsTotals.overall)}
                  </Typography>
                  <Typography>
                    Total Charges:{" "}
                    {formatCurrency(allRepairsTotals.totalCharges)}
                  </Typography>
                  {allRepairsTotals.distance != null && (
                    <Typography>
                      Total Distance:{" "}
                      {allRepairsTotals.distance.toLocaleString()} miles
                    </Typography>
                  )}
                  <Typography>
                    Maintenance Per Mile:{" "}
                    {allRepairsTotals.maintenancePerMileValue != null
                      ? `${formatCurrency(
                          allRepairsTotals.maintenancePerMileValue,
                        )} per mile`
                      : "N/A"}
                  </Typography>
                </Stack>
              )}
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Unit</TableCell>
                      <TableCell align="right">Repairs</TableCell>
                      <TableCell align="right">Total Amount</TableCell>
                      <TableCell align="right">Total Charged</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allRepairsByUnit.map((unitGroup) => {
                      const isExpanded = expandedAllRepairUnits.has(
                        unitGroup.unitNumber,
                      );

                      return (
                        <React.Fragment key={unitGroup.unitNumber}>
                          <TableRow
                            hover
                            onClick={() =>
                              handleToggleAllRepairsUnit(unitGroup.unitNumber)
                            }
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleToggleAllRepairsUnit(
                                    unitGroup.unitNumber,
                                  );
                                }}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell>{unitGroup.unitNumber}</TableCell>
                            <TableCell align="right">
                              {unitGroup.repairs.length}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(unitGroup.totalAmount)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(unitGroup.totalCharged)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              style={{ paddingBottom: 0, paddingTop: 0 }}
                              colSpan={5}
                            >
                              <Collapse
                                in={isExpanded}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ margin: 1 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1 }}
                                  >
                                    Repair Details
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell align="right">
                                          Mileage
                                        </TableCell>
                                        <TableCell>Vendor</TableCell>
                                        <TableCell align="right">
                                          Amount
                                        </TableCell>
                                        <TableCell align="right">
                                          Charged
                                        </TableCell>
                                        <TableCell>Invoice #</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {unitGroup.repairs.map(
                                        (repair, index) => (
                                          <TableRow
                                            key={`${unitGroup.unitNumber}-${repair.date}-${index}`}
                                          >
                                            <TableCell>
                                              {repair.description}
                                            </TableCell>
                                            <TableCell>
                                              {getIncidentLabel(
                                                repair.category,
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {format(
                                                parseISO(repair.date),
                                                "MM/dd/yyyy",
                                              )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {repair.mileage != null
                                                ? repair.mileage
                                                : "-"}
                                            </TableCell>
                                            <TableCell>
                                              {repair.vendor}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatCurrency(repair.amount)}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatCurrency(
                                                resolveChargedTotal(repair),
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {formatInvoiceNumbers(
                                                repair.invoiceNumber,
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ),
                                      )}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </TabPanel>
      <TabPanel
        value={tabValue}
        index={availableTabs.findIndex((tab) => tab.key === "assetFinancials")}
      >
        <Paper sx={{ p: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            sx={{ mb: 3 }}
          >
            <ToggleButtonGroup
              color="primary"
              value={financialAssetType}
              exclusive
              onChange={(_, value: AssetType | null) => {
                if (value) {
                  setFinancialAssetType(value);
                }
              }}
            >
              <ToggleButton value="truck">Trucks</ToggleButton>
              <ToggleButton value="trailer">Trailers</ToggleButton>
            </ToggleButtonGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={financialAreActive}
                  onChange={(event) =>
                    setFinancialAreActive(event.target.checked)
                  }
                />
              }
              label={`${financialAreActive ? "Active" : "Inactive"} assets`}
            />
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() => {
                const exportData = financialAssets.map((item) => ({
                  "Asset Type":
                    item.assetType === "truck" ? "Truck" : "Trailer",
                  "Unit Number": item.asset.unitNumber ?? "",
                  Make: item.asset.make ?? "",
                  Model: item.asset.model ?? "",
                  Year: item.asset.year ?? "",
                  VIN: item.asset.vin ?? "",
                  "License Plate": item.asset.licensePlate ?? "",
                  State: item.asset.state ?? "",
                  Mileage: item.asset.mileage ?? "",
                  Status: item.asset.status ?? "",
                  "Loan Flag": item.loan?.loanFlag ?? "",
                  "Lender Name": item.loan?.lenderName ?? "",
                  "Loan Number": item.loan?.loanNumber ?? "",
                  "Loan Term (Months)": item.loan?.loanTermMonths ?? "",
                  "Interest Rate": item.loan?.interestRate ?? "",
                  "Monthly Payment": item.loan?.monthlyPayment ?? "",
                  "Estimated Remaining Balance":
                    item.loan?.estimatedRemainingBalance ?? "",
                  "Estimated End Date": item.loan?.estimatedEndDate
                    ? formatDateValue(item.loan.estimatedEndDate)
                    : "",
                  "Authority Company": item.rent?.authorityCompanyName ?? "",
                  "Transaction Type": item.rent?.transactionType ?? "",
                  "Bill To Company": item.rent?.billToCompanyName ?? "",
                  "Driver Full Name": item.rent?.driverFullName ?? "",
                  "Billing Frequency": item.rent?.billingFrequency ?? "",
                  "Base Rental Rate": item.rent?.baseRentalRate ?? "",
                  "Lease Term": item.rent?.leaseTerm ?? "",
                  "Start Date": item.rent?.startDate
                    ? formatDateValue(item.rent.startDate)
                    : "",
                  "Down Payment (Reimbursable Deposit)":
                    item.rent?.reimbursableDeposit ?? "",
                  "Mileage Rate": item.rent?.mileageRate ?? "",
                }));

                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(exportData);
                XLSX.utils.book_append_sheet(wb, ws, "Asset Financials");
                XLSX.writeFile(
                  wb,
                  `Asset_Financials_${financialAssetType}_${
                    financialAreActive ? "active" : "inactive"
                  }.xlsx`,
                );
              }}
              disabled={financialAssets.length === 0}
            >
              Export to Excel
            </Button>
          </Stack>

          {financialLoading ? (
            <Box
              flexWrap={{ xs: "wrap", lg: "nowrap" }}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <CircularProgress />
            </Box>
          ) : financialError ? (
            <Typography color="error">{financialError}</Typography>
          ) : financialAssets.length === 0 ? (
            <Typography color="text.secondary">
              No assets found for this report.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Asset</TableCell>
                    <TableCell>Loan</TableCell>
                    <TableCell>Rent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {financialAssets.map((item, index) => (
                    <TableRow
                      key={`${item.asset.unitNumber ?? "asset"}-${index}`}
                    >
                      <TableCell sx={{ minWidth: 240 }}>
                        <Stack spacing={0.5}>
                          <Stack
                            flexWrap={{ xs: "wrap", lg: "nowrap" }}
                            useFlexGap
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography
                              variant="subtitle2"
                              sx={
                                item.asset.id
                                  ? {
                                      cursor: "pointer",
                                      color: "primary.main",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }
                                  : undefined
                              }
                              onClick={() => {
                                if (!item.asset.id) return;
                                navigate(
                                  `/fleet/assets/${item.assetType}/${item.asset.id}`,
                                );
                              }}
                            >
                              {item.asset.unitNumber ?? "N/A"}
                            </Typography>
                            <Chip
                              size="small"
                              label={
                                item.assetType === "truck" ? "Truck" : "Trailer"
                              }
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {[
                              item.asset.make,
                              item.asset.model,
                              item.asset.year,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            VIN: {item.asset.vin ?? "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Plate: {item.asset.licensePlate ?? "N/A"} (
                            {item.asset.state ?? "N/A"})
                          </Typography>
                          {item.assetType === "truck" && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Mileage: {item.asset.mileage ?? "N/A"}
                            </Typography>
                          )}
                          {item.asset.status && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Status: {item.asset.status}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 240 }}>
                        {item.loan ? (
                          <Stack spacing={0.5}>
                            <Typography variant="body2">
                              Flag: {item.loan.loanFlag ?? "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              Loan #: {item.loan.loanNumber ?? "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              Lender: {item.loan.lenderName ?? "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              Term: {item.loan.loanTermMonths ?? "N/A"} months
                            </Typography>
                            <Typography variant="body2">
                              Interest: {formatPercent(item.loan.interestRate)}
                            </Typography>
                            {item.loan.loanAmount !== undefined && (
                              <Typography variant="body2">
                                Amount: {formatCurrency(item.loan.loanAmount)}
                              </Typography>
                            )}
                            {item.loan.monthlyPayment !== undefined && (
                              <Typography variant="body2">
                                Monthly Payment:{" "}
                                {formatCurrency(item.loan.monthlyPayment)}
                              </Typography>
                            )}
                            {item.loan.estimatedRemainingBalance !==
                              undefined && (
                              <Typography variant="body2">
                                Estimated Remaining:{" "}
                                {formatCurrency(
                                  item.loan.estimatedRemainingBalance,
                                )}
                              </Typography>
                            )}
                            {item.loan.latestVerifiedRemainingBalance !==
                              undefined && (
                              <Typography variant="body2">
                                Remaining:{" "}
                                {formatCurrency(
                                  item.loan.latestVerifiedRemainingBalance,
                                )}
                              </Typography>
                            )}
                            {item.loan.loanStartDate && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Start:{" "}
                                {formatDateValue(item.loan.loanStartDate)}
                              </Typography>
                            )}
                            {item.loan.estimatedEndDate && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Est. End:{" "}
                                {formatDateValue(item.loan.estimatedEndDate)}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography color="text.secondary">
                            No loan
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 240 }}>
                        {item.rent ? (
                          <Stack spacing={0.5}>
                            <Typography variant="body2">
                              Authority:{" "}
                              {item.rent.authorityCompanyName ?? "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              Bill To: {item.rent.billToCompanyName ?? "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              Transaction: {item.rent.transactionType ?? "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              Driver: {item.rent.driverFullName ?? "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              Billing: {item.rent.billingFrequency ?? "N/A"}
                            </Typography>
                            {item.rent.baseRentalRate !== undefined && (
                              <Typography variant="body2">
                                Base Rate:{" "}
                                {formatCurrency(item.rent.baseRentalRate)}
                              </Typography>
                            )}
                            {item.rent.mileageRate !== undefined && (
                              <Typography variant="body2">
                                Mileage Rate:{" "}
                                {formatCurrency(item.rent.mileageRate)}
                              </Typography>
                            )}
                            {item.rent.leaseTerm !== undefined && (
                              <Typography variant="body2">
                                Lease Term: {item.rent.leaseTerm} months
                              </Typography>
                            )}
                            {item.rent.rentalTerm !== undefined && (
                              <Typography variant="body2">
                                Term: {item.rent.rentalTerm}{" "}
                                {item.rent.billingFrequency
                                  ? item.rent.billingFrequency.toLowerCase()
                                  : "units"}
                              </Typography>
                            )}
                            {item.rent.startDate && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Start: {formatDateValue(item.rent.startDate)}
                              </Typography>
                            )}
                            {item.rent.reimbursableDeposit !== undefined && (
                              <Typography variant="body2">
                                Down Payment (Reimbursable Deposit):{" "}
                                {formatCurrency(item.rent.reimbursableDeposit)}
                              </Typography>
                            )}
                            {item.rent.endDate && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                End: {formatDateValue(item.rent.endDate)}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography color="text.secondary">
                            No rent
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default Reports;
